import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PatientBilling from "components/chatbot/PatientBilling";
import { useAuth } from "hooks/useAuth";
import { useFacility } from "hooks/useFacility";
import DiscussSymptomsPage from "pages/dashboard/individual/DiscussSymptomsPage";
import UpgradePage from "pages/dashboard/UpgradePage";
import Approval from "components/dashboard/admin/Approval";
import AssistantManager from "components/dashboard/admin/AssistantManager";
import UsageMonitor from "components/dashboard/admin/UsageMonitor";
import DashboardLayout from "components/dashboard/DashboardLayout";
import Settings from "components/dashboard/Settings";
import PatientOnboardingDialog from "components/dashboard/PatientOnboardingDialog";
import ProfessionalOnboardingDialog from "components/dashboard/ProfessionalOnboardingDialog";
import HealthRecords from "components/dashboard/HealthRecords";
import MedicationsPage from "pages/dashboard/individual/MedicationsPage";
import MedicationTrackingPage from "pages/dashboard/individual/MedicationTrackingPage";
import { db } from "services/firebase";
import ChatPage from "components/chat_new/ChatPage";

const PatientRoutes = () => {
  const { user, userData, subscription, hasActiveDailyPass } = useAuth();
  const { data } = useFacility();
  const location = useLocation();
  const [showPatientOnboarding, setShowPatientOnboarding] = useState(false);
  const [showProfessionalOnboarding, setShowProfessionalOnboarding] =
    useState(false);

  useEffect(() => {
    // Check if we should show the onboarding dialog
    const checkOnboardingStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const userData = userDoc.data();

          if (userData?.role === "patient" && !userData?.hideOnboardingDialog) {
            setShowPatientOnboarding(true);
          } else if (
            userData?.role === "professional" &&
            !userData?.hideOnboardingDialog
          ) {
            setShowProfessionalOnboarding(true);
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const handleClosePatientOnboarding = () => {
    setShowPatientOnboarding(false);
  };

  const handleCloseProfessionalOnboarding = () => {
    setShowProfessionalOnboarding(false);
  };

  // Check if current route is a chat page that should be full-width
  const isChatRoute = () => {
    const path = location.pathname;
    return (
      path === "/dashboard" ||
      path === "/dashboard/" ||
      path.includes("/advanced-library") ||
      path.includes("/virtual-md") ||
      path.includes("/deepdive") ||
      path.includes("/peerview")
    );
  };

  return (
    <DashboardLayout fullWidth={isChatRoute()}>
      {/* Show the appropriate onboarding dialog based on user role */}
      <PatientOnboardingDialog
        open={showPatientOnboarding}
        onClose={handleClosePatientOnboarding}
        userId={user?.uid}
      />
      <ProfessionalOnboardingDialog
        open={showProfessionalOnboarding}
        onClose={handleCloseProfessionalOnboarding}
        userId={user?.uid}
      />

      <Routes>
        {/* Role-based default route */}
        <Route
          path="/"
          element={
            userData?.role === "professional" ? (
              <ChatPage assistantType="brainflash" />
            ) : (
              <ChatPage assistantType="basic-medical-library" />
            )
          }
        />

        {/* Patient-only routes */}
        {userData?.role === "patient" && (
          <>
            <Route
              path="/advanced-library"
              element={<ChatPage assistantType="advanced-medical-library" />}
            />

            <Route
              path="/virtual-md"
              element={<ChatPage assistantType="virtual-md" />}
            />
          </>
        )}

        {/* Professional-only routes */}
        {userData?.role === "professional" && (
          <>
            <Route
              path="/deepdive"
              element={<ChatPage assistantType="deep-dive" />}
            />
            <Route
              path="/peerview"
              element={<ChatPage assistantType="peer-review" />}
            />
          </>
        )}

        <Route
          path="/settings"
          element={<Settings uid={user?.uid} subscription={subscription} />}
        />
        <Route path="/health-records" element={<HealthRecords />} />
        <Route path="/medications" element={<MedicationsPage />} />
        <Route
          path="/medication-tracking"
          element={<MedicationTrackingPage />}
        />

        {/* Only show upgrade page if user doesn't have active daily pass */}
        {!userData?.dailyPassExpiresAt && (
          <Route path="/upgrade" element={<UpgradePage />} />
        )}

        <Route path="/admin/approval" element={<Approval />} />
        <Route path="/admin/assistants" element={<AssistantManager />} />
        <Route path="/admin/usage" element={<UsageMonitor />} />
        {/* Catch-all route to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </DashboardLayout>
  );
};

export default PatientRoutes;
